import { Injectable, inject } from '@angular/core';
import { defer, delay, Observable, of } from 'rxjs';

import type { TableQuery } from '../../../../shared/components/generic-table/models/table-query.model';
import type { Comment, CommentAuthor, CommentsServiceInterface } from '../../../../shared/components/comments/models/comments-service.interface';
import type { User } from '../../../../shared/models/user.model';
import type { TimelineEvent, TimelineEventType } from '../../components/variance-supporting-data/models/timeline-event.model';
import type { CaseInvestigationStatusLogDto } from '../models/case-investigation-status-log.model';
import type {
  CaseInvestigationHeader,
  CaseInvestigationIncidentRow,
  IncidentStatus,
  IncidentType,
} from '../models/case-investigation.models';
import { ATTACHMENT_KINDS } from '../constants/attachment-builders';
import type { NewCommentPayload } from '../../../../shared/components/comments/comments.component';
import type { CaseInvestigationClient } from '../../../../core/tokens/case-investigation-client.token';
import { ADD_COMPARISON_AS_INCIDENT_CLIENT } from '../../../../core/tokens/add-comparison-as-incident.token';

export interface TableDataResponse<T> {
  readonly data: readonly T[];
  readonly totalRecords: number;
}

@Injectable({ providedIn: 'root' })
export class CaseInvestigationMockService implements CommentsServiceInterface, CaseInvestigationClient {
  private readonly addComparisonClient = inject(ADD_COMPARISON_AS_INCIDENT_CLIENT);
  private readonly archivedRowIds = new Set<string>(['fc-003']);

  private readonly rows: readonly CaseInvestigationIncidentRow[] = [
    ...generateFeatureComparisonRows(),
    ...generateIncidentRows(200),
  ];

  private readonly users: readonly User[] = [
    {
      id: 1,
      username: 'ChuckH',
      displayName: 'Chuck Harrison',
      avatar: 'https://i.pravatar.cc/40?img=12',
      userId: '5ad32690-cbdd-4d38-9c8f-075907dbc3fc',
      firstName: null,
      lastName: null,
      email: '',
      cellNumber: null,
      midasAdmin: false,
      permissions: 0,
      employeeLevelId: null,
      otherEmployeeLevel: null,
      employeeTypeId: null,
      otherEmployeeType: null,
      normalizedUserRoleId: null,
      otherNormalizedUserRole: null,
      createdAt: '',
      lastAccessed: '',
      userGroups: [],
    },
    {
      id: 2,
      username: 'IsabellaR',
      displayName: 'Isabella Rivera',
      avatar: 'https://i.pravatar.cc/40?img=47',
      userId: '82a3f208-9c30-48c6-884f-ce9fe503bf01',
      firstName: null,
      lastName: null,
      email: '',
      cellNumber: null,
      midasAdmin: false,
      permissions: 0,
      employeeLevelId: null,
      otherEmployeeLevel: null,
      employeeTypeId: null,
      otherEmployeeType: null,
      normalizedUserRoleId: null,
      otherNormalizedUserRole: null,
      createdAt: '',
      lastAccessed: '',
      userGroups: [],
    },
    {
      id: 3,
      username: 'AlexanderG',
      displayName: 'Alexander Green',
      avatar: 'https://i.pravatar.cc/40?img=32',
      userId: 'f5e53102-0d22-427d-b213-a6fd4478b9de',
      firstName: null,
      lastName: null,
      email: '',
      cellNumber: null,
      midasAdmin: false,
      permissions: 0,
      employeeLevelId: null,
      otherEmployeeLevel: null,
      employeeTypeId: null,
      otherEmployeeType: null,
      normalizedUserRoleId: null,
      otherNormalizedUserRole: null,
      createdAt: '',
      lastAccessed: '',
      userGroups: [],
    },
  ];

  private readonly commentsByCaseId = new Map<string, Comment[]>([
    [
      '10003',
      [
        {
          id: 'c1',
          author: {
            id: 'u2',
            name: 'Isabella Rivera',
            avatarUrl: 'https://i.pravatar.cc/40?img=47',
          },
          content: '@ChuckH please review incident 10003 and confirm next steps.',
          taggedUsers: ['ChuckH'],
          createdAt: new Date('2025-12-10T13:56:00'),
        },
        {
          id: 'c2',
          author: {
            id: 'u1',
            name: 'Chuck Harrison',
            avatarUrl: 'https://i.pravatar.cc/40?img=12',
          },
          content: 'Reviewed. Marking as Active Investigation.',
          taggedUsers: [],
          createdAt: new Date('2025-12-10T14:12:00'),
        },
      ],
    ],
  ]);

  getHeader(caseId: string): Observable<CaseInvestigationHeader> {
    return defer(() =>
      of({
        employeeFirstName: 'Chuck',
        employeeLastName: 'Harrison',
        caseNumber: caseId,
        employeeId: 'CHH18',
        employeeStatus: 'Under Invest',
        role: 'Pharmacist',
        riskScore: 3.7,
        caseStatus: 'New',
      } satisfies CaseInvestigationHeader)
    ).pipe(delay(150));
  }

  getComments(caseId: string): Observable<readonly Comment[]> {
    return defer(() => of(this.commentsByCaseId.get(caseId) ?? [])).pipe(delay(150));
  }

  addComment(caseId: string, payload: NewCommentPayload, authorUserId: string): Observable<void> {
    const list = this.commentsByCaseId.get(caseId) ?? [];
    const newComment: Comment = {
      id: crypto.randomUUID(),
      author: {
        id: authorUserId,
        name: 'Chuck Harrison',
        avatarUrl: 'https://i.pravatar.cc/40?img=12',
      },
      content: payload.content,
      createdAt: new Date(),
      taggedUsers: payload.taggedUsers ?? [],
    };
    this.commentsByCaseId.set(caseId, [...list, newComment]);
    return of(undefined).pipe(delay(300));
  }

  getCurrentUser(): Observable<CommentAuthor> {
    return defer(() =>
      of({
        id: 'b9a96a86-5dd4-4029-af0f-369d37b99109',
        name: 'Chuck Harrison',
        avatarUrl: 'https://i.pravatar.cc/40?img=12',
      } satisfies CommentAuthor)
    ).pipe(delay(150));
  }

  getUsersForTagging(search?: string): Observable<User[]> {
    const term = (search ?? '').trim().toLowerCase();
    const filtered = !term
      ? [...this.users]
      : this.users.filter((u) => {
          const haystack = `${u.username} ${u.displayName}`.toLowerCase();
          return haystack.includes(term);
        });

    return defer(() => of(filtered)).pipe(delay(150));
  }

  getTimelineEvents(caseId: string): Observable<readonly TimelineEvent[]> {
    return defer(() => of(generateCaseTimelineEvents(caseId))).pipe(delay(200));
  }

  getIncidentRows(
    caseId: string,
    query: TableQuery
  ): Observable<TableDataResponse<CaseInvestigationIncidentRow>> {
    return defer(() => {
      const normalized = normalizeQuery(query);

      const addedIds = this.addComparisonClient.getAddedIncidentRowIds(caseId);
      const existingIds = new Set(this.rows.map((r) => r.id));
      const addedRows = addedIds
        .filter((id) => !existingIds.has(id))
        .map((id) =>
          buildSyntheticFeatureComparisonRow(id, this.addComparisonClient.getAddedIncidentRowPayload(caseId, id))
        );
      const allRows = [...addedRows, ...this.rows];

      const withArchived = allRows.map((r) =>
        this.archivedRowIds.has(r.id)
          ? { ...r, incidentStatus: 'Archived' as IncidentStatus }
          : r
      );

      const statusFiltered = applyIncidentStatusFilter(
        withArchived,
        normalized.filters?.['incidentStatus']
      );
      const filtered = applySearch(statusFiltered, normalized.search);
      const sorted = applySort(filtered, normalized.sortField, normalized.sortOrder);

      const start = normalized.page * normalized.rows;
      const pageData = sorted.slice(start, start + normalized.rows);

      return of({
        data: pageData,
        totalRecords: sorted.length,
      } satisfies TableDataResponse<CaseInvestigationIncidentRow>);
    }).pipe(delay(250));
  }

  updateCaseStatus(_caseId: string, _status: string, _organizationId: string): Observable<void> {
    return of(undefined).pipe(delay(200));
  }

  getStatusLogs(_caseId: number, _organizationId: number): Observable<readonly CaseInvestigationStatusLogDto[]> {
    return of([]).pipe(delay(100));
  }

  archiveRow(id: string): void {
    this.archivedRowIds.add(id);
  }

  unarchiveRow(id: string): void {
    this.archivedRowIds.delete(id);
  }

  //TODO: Temporary method to check if a row is archived (testing).
  isIncidentRowArchived(id: string): boolean {
    return this.archivedRowIds.has(id);
  }
}

function normalizeQuery(
  q: TableQuery
): Required<Pick<TableQuery, 'page' | 'rows'>> & TableQuery {
  return {
    ...q,
    page: Math.max(0, q.page ?? 0),
    rows: Math.max(1, q.rows ?? 10),
  };
}

function applyIncidentStatusFilter(
  rows: readonly CaseInvestigationIncidentRow[],
  incidentStatusFilter: string | undefined
): CaseInvestigationIncidentRow[] {
  if (!incidentStatusFilter) {
    return rows.filter((r) => r.incidentStatus !== 'Archived');
  }
  if (incidentStatusFilter === 'Archived') {
    return rows.filter((r) => r.incidentStatus === 'Archived');
  }
  return rows.filter((r) => r.incidentStatus === incidentStatusFilter);
}

function applySearch(
  rows: readonly CaseInvestigationIncidentRow[],
  search: string | undefined
): CaseInvestigationIncidentRow[] {
  const term = (search ?? '').trim().toLowerCase();
  if (!term) return [...rows];

  return rows.filter((r) => {
    const haystack = [
      r.incidentStatus,
      r.incident,
      r.medication,
      r.date,
      r.time,
      r.auditor,
      r.clinicalUnit,
      r.shift,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return haystack.includes(term);
  });
}

function applySort(
  rows: CaseInvestigationIncidentRow[],
  sortField: string | undefined,
  sortOrder: 1 | -1 | undefined
): CaseInvestigationIncidentRow[] {
  if (!sortField) return rows;
  const order: 1 | -1 = sortOrder === -1 ? -1 : 1;

  return [...rows].sort((a, b) => {
    const av = a[sortField];
    const bv = b[sortField];

    if (av == null && bv == null) return 0;
    if (av == null) return -1 * order;
    if (bv == null) return 1 * order;

    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * order;

    return String(av).localeCompare(String(bv)) * order;
  });
}

/** Build a single Feature Comparison row for an id added from the Feature Comparison page. */
export function buildSyntheticFeatureComparisonRow(
  id: string,
  payload?: { medication?: string; employeeName?: string }
): CaseInvestigationIncidentRow {
  const now = new Date();
  return {
    id,
    incident: 'Feature Comparison',
    medication: payload?.medication ?? '—',
    date: now.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
    time: now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
    auditor: payload?.employeeName ?? 'ChuckH',
    clinicalUnit: '-',
    shift: '-',
    attachments: [{ type: 'document', name: `Feature Comparison ${id}.pdf`, url: '#' }],
  } satisfies CaseInvestigationIncidentRow;
}

/** Saved Feature Comparison rows: Location & Shift = "-", Medication/Date/Time/Auditor populated. */
function generateFeatureComparisonRows(): CaseInvestigationIncidentRow[] {
  const baseDate = new Date(2025, 2, 1); // 03/01/2025
  const auditors = ['AlexanderG', 'IsabellaR', 'BenjaminL', 'SophiaM'] as const;
  const medications = ['Oxycodone', 'Lorazepam', 'Alprazolam'] as const;

  return [
    {
      id: 'fc-001',
      // incidentStatus: '',
      incident: 'Feature Comparison',
      medication: medications[0],
      date: baseDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
      time: '09:15 am',
      auditor: auditors[0],
      clinicalUnit: '-',
      shift: '-',
      attachments: [
        { type: 'document', name: "Feature Comparison: 'Ariel vs Nursery Dept 10 Oct 2025'.pdf", url: '#' },
      ],
    },
    {
      id: 'fc-002',
      // incidentStatus: 'Pending Response',
      incident: 'Feature Comparison',
      medication: medications[1],
      date: new Date(2025, 1, 28).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
      time: '02:30 pm',
      auditor: auditors[1],
      clinicalUnit: '-',
      shift: '-',
      attachments: [
        { type: 'document', name: 'Comparison_Oxycodone_Jan2025.pdf', url: '#' },
        { type: 'document', name: 'Comparison_Lorazepam_Jan2025.pdf', url: '#' },
      ],
    },
    {
      id: 'fc-003',
      incident: 'Feature Comparison',
      medication: medications[2],
      date: new Date(2025, 1, 25).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
      time: '11:00 am',
      auditor: auditors[2],
      clinicalUnit: '-',
      shift: '-',
      attachments: [{ type: 'document', name: 'Variance_Comparison_Report.pdf', url: '#' }],
    },
  ] satisfies CaseInvestigationIncidentRow[];
}

function generateIncidentRows(count: number): CaseInvestigationIncidentRow[] {
  const incidentStatuses: readonly IncidentStatus[] = [
    'Pending Response',
    'Closed',
    'Unresolved',
    'Under Investigation',
  ];

  const incidentTypes: readonly IncidentType[] = [
    'Variance Escalated',
    'Alibi Escalation',
    'RxIntercept',
    'AI Video Flag',
    'Feature Comparison',
  ];

  const medications = [
    'Oxycodone',
    'Lorazepam',
    'Alprazolam',
    'Morphine',
    'Fentanyl',
    'Hydromorphone',
  ] as const;

  const auditors = ['AlexanderG', 'IsabellaR', 'BenjaminL', 'SophiaM'] as const;
  const clinicalUnits = ['OR-2', 'CTICU', 'OR-3'] as const;
  const shifts = ['1st', '2nd', '3rd'] as const;

  const baseDate = new Date(2025, 1, 4); // 02/04/2025

  return Array.from({ length: count }, (_, i) => {
    const date = new Date(baseDate);
    date.setDate(baseDate.getDate() - (i % 30));

    const hours = (8 + (i % 12)) % 24;
    const minutes = i % 2 === 0 ? 0 : 30;
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;

    return {
      id: String(10000 + i),
      incidentStatus: incidentStatuses[i % incidentStatuses.length],
      incident: incidentTypes[i % incidentTypes.length],
      medication: medications[i % medications.length],
      date: date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      }),
      time: `${String(displayHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${ampm}`,
      auditor: auditors[i % auditors.length],
      clinicalUnit: clinicalUnits[i % clinicalUnits.length],
      shift: shifts[i % shifts.length],
      attachment: ATTACHMENT_KINDS[i % 4],
    } satisfies CaseInvestigationIncidentRow;
  });
}

function generateCaseTimelineEvents(caseId: string): readonly TimelineEvent[] {
  const caseInvestigationId = Number.parseInt(caseId, 10) || 0;

  const events: readonly TimelineEvent[] = [
    {
      id: 1,
      message: `Case ${caseId} was created`,
      actorUserId: null,
      createdAt: new Date('2025-12-09T09:10:00'),
      updatedAt: null,
      isActive: true,
      actorUser: null,
      timelineEventTarget: { id: 1, timelineEventId: 1, caseInvestigationId },
      eventType: 'StatusChange' as TimelineEventType,
      eventSource: 'System',
    },
    {
      id: 2,
      message: '@ChuckH please review incident 10003 and confirm next steps.',
      actorUserId: 2,
      createdAt: new Date('2025-12-10T13:56:00'),
      updatedAt: null,
      isActive: true,
      actorUser: { id: 2, displayName: 'Isabella Rivera' },
      timelineEventTarget: { id: 2, timelineEventId: 2, caseInvestigationId },
      eventType: 'NewComment' as TimelineEventType,
      eventSource: 'User',
    },
    {
      id: 3,
      message: 'Case Status Changed to: Active Investigation',
      actorUserId: 1,
      createdAt: new Date('2025-12-10T14:12:00'),
      updatedAt: null,
      isActive: true,
      actorUser: { id: 1, displayName: 'Chuck Harrison' },
      timelineEventTarget: { id: 3, timelineEventId: 3, caseInvestigationId },
      eventType: 'StatusChange' as TimelineEventType,
      eventSource: 'User',
    },
  ];

  return [...events].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

